import { Controller } from "@hotwired/stimulus"
import { post } from "@rails/request.js"
import { setButtonLoading } from "../lib/button_loading"

// OTP rescue popup (#11442): opens 60s after a code is sent without a
// successful verification, or immediately on a return visit. Also carries the
// bad-number state (#13666): when Twilio or the carrier reports that the number
// itself cannot take a text, the same modal opens at once with a different
// title, body and footer — one primary action, "Update Number" — instead of
// the generic help copy, so a dead number is never left to the 60 s timer.
export default class extends Controller {
  static targets = [
    "modal", "bypassBtn", "stepForm", "error",
    "title", "defaultBody", "badNumberBody", "resendBtn", "updateNumberBtn"
  ]
  static values = {
    codeAlreadySent: Boolean,
    delaySeconds: { type: Number, default: 60 },
    bypassUrl: String,
    badNumberTitle: { type: String, default: "That number can't receive texts" }
  }

  connect() {
    this.settled = false
    this.mode = "default"
    if (this.hasTitleTarget) this.defaultTitle = this.titleTarget.textContent
    if (this.codeAlreadySentValue) this.open()
  }

  disconnect() {
    this.clearTimer()
  }

  codeSent() {
    if (this.settled) return

    // A fresh send resets any bad-number verdict — it belongs to the old number.
    this.applyMode("default")
    this.clearTimer()
    this.timer = setTimeout(() => this.open(), this.delaySecondsValue * 1000)
  }

  verified() {
    this.settled = true
    this.clearTimer()
    this.hide()
  }

  // phone-verification-inline:delivery-failed — the send was refused for the
  // number itself, or the carrier reported the code text undelivered. Opens
  // immediately; the timer for the generic popup is cancelled so the two never
  // stack.
  badNumber(event) {
    if (this.settled) return

    this.clearTimer()
    this.applyMode("bad_number", event?.detail?.message)
    this.open()
  }

  open() {
    if (this.settled) return
    if (!this.hasModalTarget || !window.bootstrap) return

    this.clearError()
    window.bootstrap.Modal.getOrCreateInstance(this.modalTarget).show()
  }

  hide() {
    if (!this.hasModalTarget || !window.bootstrap) return

    window.bootstrap.Modal.getOrCreateInstance(this.modalTarget).hide()
  }

  resend() {
    this.hideThen(() => this.dispatch("resend-requested"))
  }

  openSupport() {
    this.hideThen(() => this.dispatch("support-requested"))
  }

  // Bad-number footer action: hand focus back to the phone field. Event name:
  // verification-rescue:update-number-requested.
  updateNumber() {
    this.hideThen(() => this.dispatch("update-number-requested"))
  }

  hideThen(callback) {
    const animating =
      this.hasModalTarget && window.bootstrap && this.modalTarget.classList.contains("show")

    if (!animating) {
      this.hide()
      callback()
      return
    }

    this.modalTarget.addEventListener("hidden.bs.modal", callback, { once: true })
    this.hide()
  }

  async bypass(event) {
    event.preventDefault()
    this.clearError()
    this.setBypassing(true)

    try {
      const response = await post(this.bypassUrlValue, { responseKind: "json" })
      const data = await response.json

      if (response.ok && data?.success) {
        this.settled = true
        this.clearTimer()
        this.hide()
        if (this.hasStepFormTarget) this.stepFormTarget.requestSubmit()
        return
      }

      this.showError(data?.message)
    } catch {
      this.showError()
    }
  }

  // Swap title, body and footer between the generic help state and the
  // bad-number state. Everything is toggled by target so the modal stays ONE
  // certified surface rather than a second modal.
  applyMode(mode, message) {
    this.mode = mode
    const bad = mode === "bad_number"

    if (this.hasTitleTarget) {
      this.titleTarget.textContent = bad ? this.badNumberTitleValue : this.defaultTitle
    }
    if (this.hasDefaultBodyTarget) this.defaultBodyTarget.classList.toggle("d-none", bad)
    if (this.hasBadNumberBodyTarget) {
      this.badNumberBodyTarget.classList.toggle("d-none", !bad)
      if (bad && message) this.badNumberBodyTarget.textContent = message
    }
    if (this.hasResendBtnTarget) this.resendBtnTarget.classList.toggle("d-none", bad)
    if (this.hasBypassBtnTarget) this.bypassBtnTarget.classList.toggle("d-none", bad)
    if (this.hasUpdateNumberBtnTarget) this.updateNumberBtnTarget.classList.toggle("d-none", !bad)
  }

  showError(message) {
    this.setBypassing(false)
    if (!this.hasErrorTarget) return

    this.errorTarget.textContent = message || "Something went wrong. Please try again."
    this.errorTarget.classList.remove("d-none")
  }

  clearError() {
    if (!this.hasErrorTarget) return

    this.errorTarget.textContent = ""
    this.errorTarget.classList.add("d-none")
  }

  setBypassing(bypassing) {
    if (!this.hasBypassBtnTarget) return

    // Keep "Skip" while busy; the icon-only Resend leaves room for the spinner.
    // aria-busy announces the in-flight state (BUTTON-5, #14761).
    setButtonLoading(this.bypassBtnTarget, bypassing)
  }

  clearTimer() {
    if (!this.timer) return

    clearTimeout(this.timer)
    this.timer = null
  }
}
